/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Ayi R
*/

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import ProductItem from '@components/global/ProductItem';
import ParentGroupSearch from '@components/form/search/ParentGroupSearch';
import CreateNewOption from '@/screens/setting/account-group/components/CreateNewOption';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import Table from '@components/table/Table';
import SearchTable from '@components/table/SearchTable';
import ScrollContainer from '@components/global/ScrollContainer';
import * as Layout from '@components/screen/Layout';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import {
  convertProductMarketType,
  exportCsvApi,
  hasNoAccess,
} from '@global/helpers';
import { SociollaSelectBox } from '@components/sociolla/form';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';

const { height: windowHeight } = Dimensions.get('window');

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10;

const tableStructure = {
  image: {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  product_name: {
    head: 'Product Name',
    body: 'product_name',
  },
  action: {
    head: '',
    type: 'action_icon',
    actions: [
      {
        name: 'delete',
      },
    ],
  },
};

const viewOnlyTableStructure = {
  image: tableStructure.image,
  product_name: tableStructure.product_name,
};

const limit = 10;

const AccountGroupFormScreen = (props) => {
  const dispatch = useDispatch();
  const isShowOption = useSelector((state) => state.modal.marketCreateOption);
  const isViewOnly = !hasNoAccess(['distributor_manager']);

  const [detailData, setDetailData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [products, setProducts] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [activeProductTab, setActiveProductTab] = useState('premium');
  const [isPolling, setIsPolling] = useState(false);

  const pollTimerRef = useRef(null);

  const FormValidationSchema = Yup.object().shape({
    code: Yup.string().required('Account group code is required.'),
    parent_group: Yup.object().required('Parent group is required.'),
    is_active: Yup.object().required('Active/In Active is required.'),
  });

  const getMarketTypeDetail = useApi(MsB2b.getMarketTypeDetail);

  const getMarketTypeProduct = async (
    id,
    businessID,
    accountID,
    searchVal = '',
    newSkip = 0,
    isPremium = true,
  ) => {
    const isMore = newSkip > 0;
    try {
      setSkip(newSkip);
      if (searchVal && searchVal.trim() !== '') {
        setSearchQuery(searchVal);
        setIsSearch(true);
      } else {
        setSearchQuery('');
        setIsSearch(false);
      }
      if (isMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = {
        filter: {
          account_id: accountID,
          is_premium: isPremium,
        },
        skip: newSkip,
        limit: limit,
      };
      if (searchVal) {
        params.filter.$or = [
          { 'product.name': { $regex: searchVal, $options: 'i' } },
          { 'brand.name': { $regex: searchVal, $options: 'i' } },
          { 'combination.reference': { $regex: searchVal, $options: 'i' } },
          { 'combination.ean_no': { $regex: searchVal, $options: 'i' } },
        ];
      }
      const response = await MsB2b.getProducts(id, businessID, {
        params: params,
      });
      if (response?.data?.data) {
        const result = response.data.data.map((item) => {
          item = convertProductMarketType(item);
          return item;
        });
        if (isMore) {
          const finalResult = products.concat(result);
          setProducts(finalResult);
        } else {
          setProducts(result);
        }
      } else {
        if (!isMore) {
          setProducts([]);
        }
      }
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getDetail = async (id, isPremium = true) => {
    const response = await getMarketTypeDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      result.businessID = result.parent_group.id;
      result.marketTypeID = result.parent_group.parent_id;
      result.accountID = result._id;
      result.is_active = result.is_active
        ? { label: 'Active', value: true }
        : { label: 'In Active', value: false };

      getMarketTypeProduct(
        result.marketTypeID,
        result.id,
        result.accountID,
        '',
        0,
        isPremium,
      );
      setDetailData(result);
    }
  };

  const pollForUpdates = (isPremium, previousCount) => {
    let attempts = 0;
    setIsPolling(true);

    const attempt = async () => {
      attempts += 1;
      try {
        const response = await MsB2b.getProducts(
          detailData.marketTypeID,
          detailData.id,
          {
            params: {
              filter: {
                account_id: detailData.accountID,
                is_premium: isPremium,
              },
              skip: 0,
              limit: limit,
            },
          },
        );
        const newCount = response?.data?.data?.length || 0;
        if (newCount !== previousCount) {
          getMarketTypeProduct(
            detailData.marketTypeID,
            detailData.id,
            detailData.accountID,
            '',
            0,
            isPremium,
          );
          setIsPolling(false);
          Toast.show({
            text1: 'Products imported successfully.',
            type: 'success',
          });
          return;
        }
      } catch (_err) {
        // silently ignore fetch errors during polling
      }

      if (attempts < POLL_MAX_ATTEMPTS) {
        pollTimerRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
      } else {
        setIsPolling(false);
        Toast.show({
          text1:
            'Import is taking longer than expected. Please refresh once you receive the email.',
          type: 'info',
        });
      }
    };

    pollTimerRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
  };

  const goBack = () => {
    props.navigation.replace('AccountGroupFormScreen', {});
    props.navigation.navigate('AccountGroupScreen');
  };

  const processMarketTypeProduct = async (data, is_premium = true) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const response = await MsB2b.actionMarketTypeProduct(
        detailData.marketTypeID,
        detailData.businessID,
        {
          product_id: data.id,
          combination_id: data.product_combination_id,
          is_premium: is_premium,
          market_type: {
            id: detailData.id,
            code: detailData.parent_group.parent_code,
            business_type: {
              id: detailData.parent_group.id,
              code: detailData.parent_group.code,
            },
          },
          account_id: detailData.accountID,
        },
      );
      if (response?.data?.success) {
        Toast.show({
          text1: 'Successfully added product',
          type: 'success',
        });
        getMarketTypeProduct(
          detailData.marketTypeID,
          detailData.id,
          detailData.accountID,
          '',
          0,
          is_premium,
        );
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: err?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const deleteMarketTypeProduct = async (data) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const response = await MsB2b.deleteMarketTypeProduct(
        detailData.marketTypeID,
        detailData.businessID,
        detailData.accountID,
        data.product.id,
        data.product_combination_id,
      );
      if (response?.data?.data?._id) {
        const newProduct = JSON.parse(JSON.stringify(products));
        const idx = newProduct.findIndex(
          (item) => item.product.id == data.product.id,
        );
        if (idx > -1) {
          newProduct.splice(idx, 1);
          setProducts(newProduct);
          Toast.show({
            text1: 'Successfully deleted product',
            type: 'success',
          });
        }
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: err?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onSelectProduct = (data, is_bulk = false) => {
    dispatch(showPopup({ name: 'marketCreateOption', value: false }));
    if (!is_bulk) {
      processMarketTypeProduct(data, activeProductTab === 'premium');
    } else {
      const isPremium = activeProductTab === 'premium';
      const previousCount = products.length;
      pollForUpdates(isPremium, previousCount);
    }
  };

  const onRemoveProduct = (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: () => deleteMarketTypeProduct(data),
      }),
    );
  };

  const onMapAction = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        onRemoveProduct(data);
      }
    }
  };

  const onExportAssortment = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await exportCsvApi(
        'product-assortment',
        { account_id: detailData.accountID },
        {
          isDownload: false,
          message: 'Export in progress, the result will be sent to your email.',
        },
      );
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setSkip(0);
      setIsEdit(false);
      setDetailData({});
      setActiveProductTab('premium');
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [props]);

  return (
    <Layout.Container>
      {isEdit && getMarketTypeDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            code: isEdit && detailData?.code ? detailData.code : '',
            parent_group:
              isEdit && detailData?.parent_group
                ? detailData?.parent_group
                : '',
            is_active: isEdit ? detailData?.is_active : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              payload.is_active = payload.is_active.value;
              if (!isEdit) {
                const createData = await MsB2b.createMarketType(payload);
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  props.navigation.navigate('AccountGroupScreen');
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const updateData = await MsB2b.updateMarketType(
                  detailData._id,
                  payload,
                );
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate('AccountGroupScreen');
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title="Account Group"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    title={`${isEdit ? `Edit` : `Add`} Account Group`}>
                    <View style={styles.row}>
                      <Button
                        type="default"
                        rounded
                        buttonStyle={{ width: 120, marginRight: 10 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{ fontSize: 14, letterSpacing: 0.5 }}>
                            CANCEL
                          </SociollaText>
                        }
                        onPress={() => goBack()}
                      />
                      <Button
                        type="primary"
                        rounded
                        disabled={isViewOnly}
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            SAVE
                          </SociollaText>
                        }
                        onPress={handleSubmit}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <ScrollContainer
                  style={{
                    ...(isWeb ? {} : { height: windowHeight * 0.8 }),
                  }}>
                  <Layout.FormWrapper
                    style={{
                      width: '100%',
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      flexDirection: isWeb ? 'row' : 'column',
                      justifyContent: 'space-between',
                      ...(isWeb
                        ? {
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {}),
                    }}>
                    <View
                      style={{ width: isWeb ? 'calc(50% - 15px)' : '100%' }}>
                      <Layout.FormGroup>
                        <Input
                          label="Code"
                          value={values.code}
                          isRequired
                          placeholder="Code"
                          hasError={
                            errors.code && touched.code ? errors.code : null
                          }
                          onChangeText={(val) => {
                            setFieldValue('code', val);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          isTransparent={true}
                          label="Active/Inactive"
                          options={[
                            {
                              label: 'Active',
                              value: true,
                            },
                            {
                              label: 'In Active',
                              value: false,
                            },
                          ]}
                          name="is_active"
                          trackBy="value"
                          labelKey="label"
                          isRequired
                          value={values.is_active}
                          onChange={(val) => {
                            setFieldValue('is_active', val);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                    <View
                      style={{ width: isWeb ? 'calc(50% - 15px)' : '100%' }}>
                      <Layout.FormGroup>
                        <ParentGroupSearch
                          label="Parent Group"
                          value={values.parent_group}
                          isRequired
                          onChange={(val) => {
                            setFieldValue('parent_group', val);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                  </Layout.FormWrapper>
                  {isEdit ? (
                    <>
                      <TitleSubSection
                        title="PRODUCT"
                        disabled={loading || isViewOnly}
                        button={
                          isWeb
                            ? false
                            : {
                                label: 'Add Product',
                                callback: () =>
                                  dispatch(
                                    showPopup({
                                      name: 'marketCreateOption',
                                      value: true,
                                    }),
                                  ),
                              }
                        }
                        customButton={
                          isWeb ? (
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                              <Button
                                type="outline-primary"
                                rounded
                                buttonStyle={{ width: 120 }}
                                title={
                                  <SociollaText
                                    type="medium"
                                    style={{
                                      fontSize: 14,
                                      color: Colors.blue1,
                                    }}>
                                    Export
                                  </SociollaText>
                                }
                                onPress={onExportAssortment}
                              />
                              <Button
                                type="default"
                                rounded
                                disabled={isViewOnly}
                                buttonStyle={{ width: 150 }}
                                title={
                                  <SociollaText
                                    type="bold"
                                    style={{
                                      fontSize: 14,
                                      letterSpacing: 0.5,
                                    }}>
                                    Add Product
                                  </SociollaText>
                                }
                                onPress={() =>
                                  dispatch(
                                    showPopup({
                                      name: 'marketCreateOption',
                                      value: true,
                                    }),
                                  )
                                }
                              />
                            </View>
                          ) : (
                            false
                          )
                        }
                      />

                      {/* Polling banner */}
                      {isPolling ? (
                        <View
                          style={[
                            styles.pollingBanner,
                            { marginHorizontal: isWeb ? 20 : 15 },
                          ]}>
                          <SociollaText style={styles.pollingText}>
                            Importing products, please wait...
                          </SociollaText>
                        </View>
                      ) : null}

                      <View
                        style={[
                          styles.tabContainer,
                          { marginHorizontal: isWeb ? 20 : 15 },
                        ]}>
                        <TouchableOpacity
                          style={[
                            styles.tab,
                            !isWeb && { flex: 1, alignItems: 'center' },
                            activeProductTab === 'premium' && styles.activeTab,
                          ]}
                          onPress={() => {
                            setActiveProductTab('premium');
                            getMarketTypeProduct(
                              detailData.marketTypeID,
                              detailData.id,
                              detailData.accountID,
                              '',
                              0,
                              true,
                            );
                          }}>
                          <SociollaText
                            type={
                              activeProductTab === 'premium'
                                ? 'medium'
                                : undefined
                            }
                            style={{
                              fontSize: 14,
                              color:
                                activeProductTab === 'premium'
                                  ? Colors.black3
                                  : Colors.grey2,
                            }}>
                            Premium
                          </SociollaText>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.tab,
                            !isWeb && { flex: 1, alignItems: 'center' },
                            activeProductTab === 'non_premium' &&
                              styles.activeTab,
                          ]}
                          onPress={() => {
                            setActiveProductTab('non_premium');
                            getMarketTypeProduct(
                              detailData.marketTypeID,
                              detailData.id,
                              detailData.accountID,
                              '',
                              0,
                              false,
                            );
                          }}>
                          <SociollaText
                            type={
                              activeProductTab === 'non_premium'
                                ? 'medium'
                                : undefined
                            }
                            style={{
                              fontSize: 14,
                              color:
                                activeProductTab === 'non_premium'
                                  ? Colors.black3
                                  : Colors.grey2,
                            }}>
                            Non Premium
                          </SociollaText>
                        </TouchableOpacity>
                      </View>
                      <Layout.FormWrapper
                        style={{
                          paddingVertical: 10,
                          paddingHorizontal: isWeb ? 20 : 15,
                          ...(!products?.length
                            ? { justifyContent: 'center' }
                            : {}),
                        }}>
                        {!isWeb ? (
                          <View style={{ width: '100%', paddingBottom: 10 }}>
                            <SearchTable
                              testID="account-group-product-search"
                              value={isSearch ? searchQuery : ''}
                              width="100%"
                              withBorder={true}
                              searchPlaceholder="Search by Name, Brand, Reference, EAN"
                              onSearch={(value) =>
                                getMarketTypeProduct(
                                  detailData.marketTypeID,
                                  detailData.id,
                                  detailData.accountID,
                                  value,
                                  0,
                                  activeProductTab === 'premium',
                                )
                              }
                            />
                          </View>
                        ) : (
                          false
                        )}
                        {loading ? (
                          <Layout.Loader />
                        ) : isWeb ? (
                          <Table
                            structures={
                              isViewOnly
                                ? viewOnlyTableStructure
                                : tableStructure
                            }
                            data={products}
                            withFetch={false}
                            hasNoHide
                            showFilter={false}
                            showPagination={false}
                            withoutShadow
                            totalData={products.length}
                            showSearch={true}
                            searchBorder={true}
                            searchPlaceholder="Search by Name, Brand, Reference, EAN"
                            searchKey={['name', 'reference']}
                            onSearch={(value) =>
                              getMarketTypeProduct(
                                detailData.marketTypeID,
                                detailData.id,
                                detailData.accountID,
                                value,
                                0,
                                activeProductTab === 'premium',
                              )
                            }
                            onMapAction={(val) => onMapAction(val)}
                          />
                        ) : products?.length ? (
                          products.map((item, idx) => (
                            <ProductItem
                              data={item}
                              onRemove={() => onRemoveProduct(item)}
                              showRemove={!isViewOnly}
                              isDetail
                              withoutPrice
                              key={idx}
                            />
                          ))
                        ) : (
                          <View
                            style={{
                              alignItems: 'center',
                              paddingVertical: 20,
                            }}>
                            <SociollaText style={{ color: Colors.grey2 }}>
                              No product found
                            </SociollaText>
                          </View>
                        )}
                        {loadingMore ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Layout.Loader />
                          </View>
                        ) : !loadingMore &&
                          products?.length >= limit &&
                          skip <= products?.length ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Button
                              rounded
                              type="primary"
                              buttonStyle={{ width: 150 }}
                              title={
                                <SociollaText
                                  type="bold"
                                  style={{
                                    fontSize: 13,
                                    letterSpacing: 0.5,
                                    color: Colors.white,
                                  }}>
                                  Load More
                                </SociollaText>
                              }
                              onPress={() =>
                                getMarketTypeProduct(
                                  detailData.marketTypeID,
                                  detailData.id,
                                  detailData.accountID,
                                  isSearch ? searchQuery : '',
                                  skip + limit,
                                  activeProductTab === 'premium',
                                )
                              }
                            />
                          </View>
                        ) : (
                          false
                        )}
                      </Layout.FormWrapper>
                    </>
                  ) : (
                    false
                  )}
                </ScrollContainer>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded
                    disabled={isViewOnly}
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        SAVE
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
      <CreateNewOption
        {...props}
        show={isShowOption}
        isPremium={activeProductTab === 'premium'}
        onCallback={(val) => onSelectProduct(val)}
        onCallbackBulk={(val) => onSelectProduct(val, true)}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey7,
    marginBottom: 5,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: -1,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: Colors.blue1,
  },
  buttonWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
  },
  pollingBanner: {
    backgroundColor: Colors.pollingBannerBg,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: Colors.pollingBannerBorder,
  },
  pollingText: {
    fontSize: 13,
    color: Colors.pollingBannerText,
  },
});

export default AccountGroupFormScreen;
